import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useRouter } from "next/router"
import Link from "next/link"
import { GetServerSidePropsContext } from "next"
import dynamic from "next/dynamic"
import { addDays } from "date-fns"
import upperFirst from "lodash/upperFirst"
import { useDispatch } from "store"
import { fetchUserAppointments, fetchSimplifiedQuestionnaires } from "store/global/actions"
import { useCurrentUser, useNotificationsLoading, useUnreadNotifications } from "store/hooks"
import { markAsRead } from "store/notifications/actions"
import { fetchEventsForTherapist, TherapistEvent } from "api/Therapist"
import { NotificationCategories } from "types/Misc"
import { PortalNotificationData } from "@sequel-care/types"
import { classNames, getUserName } from "utils"
import { createFetchRange } from "utils/dates"
import { EventUpdateTracker } from "utils/EventUpdateTracker"
import { FeatureFlags } from "utils/FeatureFlags"
import { getDateLocaleFormat, DATE_FORMAT } from "utils/date"
import { useNotificationRanges } from "components/Notifications/utils"
import { useDashboardPatients } from "components/TherapistDashboard/utils"
import { Button, Toggle } from "components/common"
import Badge from "components/common/Badge"
import DashboardCard from "components/DashboardCard"
import NotificationItem from "components/Notifications/NotificationItem"
import { ActivityCard, Caseload, HighBurden, TrendProgress } from "components/TherapistDashboard/Cards"
import { TherapistQuestionnaire, PastAppointments, UpcomingAppointments } from "components/TherapistHomepage"
const TherapistQuestionnaireModal = dynamic(
    () => import("components/TherapistHomepage/TherapistQuestionnaireModal/TherapistQuestionnaireModal")
)
import Patients from "./patients"
import { DateRangeFormData, DateRangeModal } from "components/v2/components/DateRangeModal"
import { useDataExport } from "hooks/useDataExport"

const Home = ({ modalId }: { modalId: number }) => {
    // comments 9
    const { t, i18n } = useTranslation("therapist")
    const { patients, loading } = useDashboardPatients("current", "case_manager")
    const [appointmentLoading, setLoading] = useState(true)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [events, setEvents] = useState<TherapistEvent[]>([])
    const router = useRouter()
    const dispatch = useDispatch()
    const user = useCurrentUser()
    const date = getDateLocaleFormat(new Date(), DATE_FORMAT, i18n)
    console.log("Therapist homepage")
    useEffect(() => {
        const getTherapistQuestionnaires = async () => {
            const response = await fetchEventsForTherapist()
            setEvents(response)
        }
        getTherapistQuestionnaires()

        // Fetch questionnaires for notifications
        dispatch(fetchSimplifiedQuestionnaires())
    }, [])

    useEffect(() => {
        setIsModalOpen(!!modalId)
    }, [modalId])

    const closeModal = () => {
        router.push("/", undefined, { shallow: true })
        setIsModalOpen(false)
    }

    const openModal = (eventId: number, date_on_timeline: string) => {
        router.push(`/theraquest/${eventId}/${date_on_timeline}`, undefined, { shallow: false })
        setIsModalOpen(true)
    }

    useEffect(() => {
        const doFetch = async () => {
            setLoading(true)
            const { start, end } = createFetchRange(({ dayEnd, dayStart }) => {
                return { start: dayStart, end: addDays(dayEnd, 1) }
            })
            await dispatch(fetchUserAppointments(start, end))
            setLoading(false)
        }

        doFetch()
        return EventUpdateTracker.subscribe(({ type }) => type === "appointment" && doFetch())
    }, [])

    const updateEventsList = (eventId: number) => {
        setEvents(events.filter((event) => event.id != eventId))
    }

    if (FeatureFlags.isDisabled("therapist-homepage")) return <Patients />

    return (
        <>
            <div className="p-10 bg-bg flex flex-col gap-11 text-dark-blue">
                <div className="flex flex-col gap-4">
                    <h1 className="text-3xl font-semibold">
                        {t("homePage.welcomeBack", { fullName: getUserName(user) })}
                    </h1>
                    <span className="text-lg text-text-blue">{date}</span>
                </div>
                <div className="flex gap-6">
                    <div className="flex flex-col gap-6 w-1/2">
                        <h2 className="text-2xl">{t("homePage.alerts")}</h2>
                        <NotificationCard category={NotificationCategories.Alerts} />
                        <TherapistQuestionnaire openModal={openModal} events={events} />
                        {FeatureFlags.isEnabled("appointment-events") && (
                            <>
                                <PastAppointments />
                                <UpcomingAppointments loading={appointmentLoading} />
                            </>
                        )}
                        <NotificationCard category={NotificationCategories.Comments} />
                    </div>
                    <div className="flex flex-col gap-6 w-1/2">
                        <div className="flex justify-between">
                            <h2 className="text-2xl">{t("homePage.patientData")}</h2>
                            <Link href={"/dashboard"} passHref>
                                <Button theme="secondary_borderless" className="!p-0 !text-base">
                                    {t("common:learnMore")}
                                </Button>
                            </Link>
                        </div>
                        <div className="flex flex-col gap-6">
                            <TrendProgress loading={loading} patients={patients} metric={"primary_progress"} />
                            <HighBurden loading={loading} patients={patients} />
                            <ActivityCard loading={loading} patients={patients} filters={undefined} />
                            <Caseload loading={loading} patients={patients} />
                        </div>
                    </div>
                </div>
            </div>
            <TherapistQuestionnaireModal
                isOpen={isModalOpen}
                onClose={closeModal}
                updateEventsList={updateEventsList}
            />
        </>
    )
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
    const urlParams = context?.params?.index || []
    const isTheraquest = urlParams.includes("theraquest")
    const modalId = urlParams[1]
    return {
        props: {
            modalId: isTheraquest ? modalId : null
        }
    }
}

export default Home

const NotificationCard = ({ category }: { category: NotificationCategories }) => {
    const { t } = useTranslation(["therapist", "common"])

    const unreadNotifications = useUnreadNotifications()

    const { getBiWeeklyAnalyticReport, getMonthlyMinistryHealthReport } = useDataExport()

    const notificationsLoading = useNotificationsLoading()
    const [selectedNotification, setSelectedNotification] = useState<PortalNotificationData | null>(null)
    const [loading, setLoading] = useState(false)
    const dispatch = useDispatch()

    const markCategoryAsRead = async () => {
        setLoading(true)
        const unreadIds = unreadNotifications[category].map((data) => data?.id)

        await dispatch(markAsRead(unreadIds))
        setLoading(false)
    }

    const onSelectNotification = (notification: PortalNotificationData) => {
        setSelectedNotification(notification)
    }

    const notificationRangeMapping = useNotificationRanges(unreadNotifications[category], t)
    const minItemsToDisplay = 3
    const notificationList = useMemo(
        () => (notificationsLoading ? new Array(minItemsToDisplay).fill(undefined) : unreadNotifications[category]),
        [unreadNotifications, category, notificationsLoading]
    )

    const isUnreadNotificationEmpty = !unreadNotifications[category] || unreadNotifications[category].length === 0

    const onSubmitDateRangeModal = async (data: DateRangeFormData) => {
        if (!selectedNotification) return

        try {
            const payload = {
                organizationId: selectedNotification.ref_data?.organization_id,
                organizationName: String(selectedNotification.ref_data?.organization_name || ""),
                startDate: data.startDate,
                endDate: data.endDate,
                questionnaireId: data.questionnaireId,
                format: data.format
            }

            if (selectedNotification.type === "data_export_ready") {
                await getBiWeeklyAnalyticReport(payload)
            } else if (selectedNotification.type === "report_ministry_of_health") {
                await getMonthlyMinistryHealthReport(payload)
            }

            setSelectedNotification(null)
        } catch (error) {
            console.error("Error downloading report:", error)
            throw error
        }
    }

    const proceedText =
        selectedNotification?.type === "report_ministry_of_health" ? t("common:downloadPDF") : t("common:downloadCSV")

    return (
        <>
            <DashboardCard
                title={
                    <>
                        <span className="text-lg font-medium">{t(`homePage.unread${upperFirst(category)}`)}</span>
                        <Badge
                            displayNumber={unreadNotifications[category].length}
                            visible={unreadNotifications[category].length > 0}
                            wrapperClassNames="text-med-blue !text-xs"
                        />
                    </>
                }
                headerRight={
                    <Button
                        theme="secondary_borderless"
                        loading={loading}
                        onClick={markCategoryAsRead}
                        disabled={isUnreadNotificationEmpty}
                        className={classNames(
                            isUnreadNotificationEmpty && "!text-border-blue cursor-default",
                            "translate-y-1.5 !p-0"
                        )}
                    >
                        {t("homePage.markAsRead")}
                    </Button>
                }
                className="!p-5"
            >
                <div>
                    {notificationsLoading || unreadNotifications[category].length ? (
                        <Toggle>
                            <Toggle.Content
                                transitionDuration={1000}
                                minChildrenToDisplay={minItemsToDisplay}
                                key={notificationsLoading ? "loading" : "loaded"}
                                maxHeight={800}
                                className="pr-2"
                            >
                                {notificationList.map((notification, index) => (
                                    <NotificationItem
                                        key={notification?.id ?? index}
                                        notification={notification}
                                        rangeTitle={notificationRangeMapping[index]}
                                        withSeparator={!(index === 0 || Boolean(notificationRangeMapping[index]))}
                                        onSelectNotification={onSelectNotification}
                                    />
                                ))}
                            </Toggle.Content>
                            {minItemsToDisplay < notificationList.length && (
                                <Toggle.Button
                                    chevron={{ closedRotation: "bottom", openRotation: "top" }}
                                    showChevron={true}
                                    className="!justify-end gap-2 text-sm items-center hover:underline underline-offset-4"
                                >
                                    {({ isOpen }) => t(`homePage.${isOpen ? "showLess" : "showMore"}`)}
                                </Toggle.Button>
                            )}
                        </Toggle>
                    ) : (
                        <div className="flex flex-col items-center m-14">
                            {t("homePage.emptyStates.alerts.title")}
                            <span className="text-secondary-2">{t(`homePage.emptyStates.${category}.subtitle`)}</span>
                        </div>
                    )}
                </div>
            </DashboardCard>

            {selectedNotification && (
                <DateRangeModal
                    isOpen={!!selectedNotification}
                    onClose={() => setSelectedNotification(null)}
                    initialStartDate={String(selectedNotification.ref_data?.start_date || "")}
                    initialEndDate={String(selectedNotification.ref_data?.end_date || "")}
                    onSubmit={onSubmitDateRangeModal}
                    proceedText={proceedText}
                    selectedNotification={selectedNotification}
                />
            )}
        </>
    )
}